import _ from 'lodash';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import './GradientText.css';

type GradientStop = {
    color: string;
    /**
     * 해당 컬러의 "시작점" 위치
     *
     * 예를 들어, 30% 지점에서 red, blue 가 교차되길 원한다면,
     * { color: 'red' } 와 { color: 'blue', position: '30%' } 를 지정해주면 됨
     */
    position: string;
};

type FirstGradientStop = {
    color: string;
    position?: never;
};

type GradientTextProps = {
    className?: string;
    children?: React.ReactNode;
    direction?: 'right' | 'left' | 'top' | 'bottom';
    /**
     * 그래디언트 형태
     *
     * - soft: 부드러운 그래디언트
     * - hard: 컬러가 딱 떨어져서 적용됨
     */
    gradientShape?: 'soft' | 'hard';
    /**
     * 그래디언트의 컬러와 위치를 지정해주는 배열
     * - 첫번째 스탑은 그래디언트의 시작이기 때문에 position 을 지정하지 않음
     * - position 은 그래디언트가 '시작'하는 위치임
     */
    stops: [FirstGradientStop, ...GradientStop[]] | [FirstGradientStop];
};

const MIN_STOPS = 1;
const MAX_STOPS = 4;

/**
 * 텍스트에 그래디언트 컬러를 적용해주는 컴포넌해
 */
export const GradientText = ({
    children,
    direction = 'right',
    gradientShape = 'soft',
    stops,
    className,
}: GradientTextProps) => {
    if (stops?.length < MIN_STOPS || stops?.length > MAX_STOPS) {
        throw new Error(`stops 배열의 길이는 ${MIN_STOPS} 이상 ${MAX_STOPS} 이하여야 합니다.`);
    }

    const style = useMemo(() => {
        const stopsVariables = {} as Record<string, string>;

        _.forEach(stops, (stop, index) => {
            stopsVariables[`--stop-${index}-color`] = stop.color;
            if (stop.position) {
                stopsVariables[`--stop-${index}-position`] = stop.position;
            }
        });

        return {
            '--direction': direction,
            ...stopsVariables,
        } as React.CSSProperties;
    }, [direction, stops]);

    return (
        <div
            className={twMerge(
                // 배경 클립 기능을 이용해서 적용 텍스트 색상은 투명하게 설정해줘야 함
                // @see GradientText.css
                'gradient-text',
                gradientShape === 'soft' && `gradient-text-soft-${stops.length}`,
                gradientShape === 'hard' && `gradient-text-hard-${stops.length}`,
                className,
            )}
            style={style}
        >
            {children}
        </div>
    );
};
